import {sharedCampaigns} from '@cl/shared';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {
  DTProductSelectionNode,
  DTSequenceNode,
  ProductMatchingOperator,
  ProductMatchingOperatorDescription,
  SequenceNodeOutputData,
  WeightedNodeOutputData,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import {DTOptimizationNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {DTConditionNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {DTCreativeNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {ContentFeedNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {DTStoreNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {DTWeightedNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {DTScheduleNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {ScheduleNodeOutputData} from 'common/src/modules/decisionTree/decisionTree.types';
import {ScheduleType} from 'common/src/modules/decisionTree/decisionTree.types';
import {ScheduleTypeDescription} from 'common/src/modules/decisionTree/decisionTree.types';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {OptimizeByName, OptimizeByValue} from 'common/src/modules/optimization/optimization.types';
import {ContentSelectionStrategyValue} from 'common/src/modules/contentFeeds/contentFeeds.enums';
import {CLEnumKeys, CLEnumType} from 'common/src/modules/enums/enums.types';
import {loadedEnumsSubject} from 'common/src/modules/shared/clinchEnums';
import getOutputTypePhrase from './getOutputTypePhrase';
import getDestinationCardPhrase from './getDestinationCardPhrase';
import {CreativeTemplateFeature} from 'common/src/modules/creativeTemplates/creativeTemplates.types';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import {ProductType, productTypeUINamesByValue} from 'common/src/modules/products/products.enums';

/***
 * Get a summary of a node
 * @param nodeId - The id of the node
 * @returns A summary of the node
 * according to the node type, provide summary of the node (logics, assets, etc)
 */
export default function getNodeSummary(nodeId: number): string {
  const node = sharedCampaigns.nodesRef.value[nodeId];

  if (!node) {
    return '';
  }

  let nodeDetails = `Card Id: ${node.id}
Name: ${node.name || `${cardTypeToName[node.type]} card`}
Type: ${cardTypeToName[node.type]}`;

  switch (node.type) {
    case NodeType.PRODUCTS: {
      const productsNode = node as DTProductSelectionNode;
      nodeDetails = nodeDetails.concat(`\nFeed: ${productsNode.feed?.name}`);
      nodeDetails = nodeDetails.concat(`\nFeed ID: ${productsNode.feed?.id} \n`);
      nodeDetails = nodeDetails.concat(
        `Product Type: ${
          productTypeUINamesByValue[productsNode.feed?.productType as ProductType]
        } \n`,
      );
      nodeDetails = nodeDetails = nodeDetails.concat(
        `Selection Strategy: ${
          loadedEnumsSubject.data[CLEnumType.CampaignPage]?.[
            CLEnumKeys.CampaignPage.CampaignContentType
          ]?.byValue[productsNode.productsSelectionStrategy]?.uiName
        } \n`,
      );

      if (
        productsNode.productsSelectionStrategy === ProductSelectionStrategyType.ProductMatching &&
        productsNode.configuration
      ) {
        nodeDetails = nodeDetails.concat(
          `\nPurpose: Select products by: ${productsNode.configuration.conditions
            .map((condition) => `${condition.feedColumnSignalKey}`)
            .join(' And ')} \n`,
        );
      }

      return nodeDetails.concat(
        productsNode.outputs
          .map((output) => {
            const outputType = getOutputTypePhrase(output);

            return `Output ${_getOutputIdentifier(output)} ${outputType} ${getDestinationCardPhrase(
              output.nodeId,
            )}`;
          })
          .join('\n'),
      );
    }

    case NodeType.WEIGHTED: {
      const weightedNode = node as DTWeightedNode;

      return nodeDetails.concat(
        `
Audience Split: ${weightedNode.shouldSplitAudience ? 'True' : 'False'}
Purpose: Select random output ${
          weightedNode.shouldSplitAudience ? 'with audience split' : ''
        }. \n\n`.concat(
          weightedNode.outputs
            .map(
              (output) =>
                `Output ${_getOutputIdentifier(output)} ${getDestinationCardPhrase(
                  output.nodeId,
                )}, Weight: ${(output as WeightedNodeOutputData).weight}`,
            )
            .join('\n'),
        ),
      );
    }

    case NodeType.GENERAL_OPTIMIZATION: {
      const optimizationNode = node as DTOptimizationNode;
      const configuration = optimizationNode.configuration;

      return nodeDetails.concat(
        `
Purpose: Optimize which output to use by ${getMatchingEnumValue(
          OptimizeByValue,
          OptimizeByName,
          configuration?.optimizeBy as OptimizeByValue,
        )} \n Outputs:\n`.concat(
          optimizationNode.outputs
            .map(
              (output) =>
                `Output ${_getOutputIdentifier(output)} ${getDestinationCardPhrase(output.nodeId)}`,
            )
            .join('\n'),
        ),
      );
    }

    case NodeType.CONDITION: {
      const conditionNode = node as DTConditionNode;

      return nodeDetails.concat(
        `
Purpose: Choose output by ${conditionNode.signalUIName ?? conditionNode.condKeyUIName} \n`.concat(
          conditionNode.outputs
            .map(
              (output) =>
                `Output ${_getOutputIdentifier(output)} ${getDestinationCardPhrase(
                  output.nodeId,
                )}. ${
                  output.type === OutputType.Fallback
                    ? 'Fallback output, automatically added by the system and should not be edited'
                    : ''
                }`,
            )
            .join('\n'),
        ),
      );
    }

    case NodeType.CREATIVE:

    case NodeType.CONCEPT_CREATIVE: {
      const creativeNode = node as DTCreativeNode;
      const creative = creativeNode.creative;

      const features = creative?.features || [];

      const supportedFeatures: string[] = [];

      if (features.includes(CreativeTemplateFeature.Feed)) {
        supportedFeatures.push('products');
      }

      if (features.includes(CreativeTemplateFeature.Location)) {
        supportedFeatures.push('location/store');
      }

      if (features.includes(CreativeTemplateFeature.Content)) {
        supportedFeatures.push('content');
      }

      if (features.includes(CreativeTemplateFeature.Weather)) {
        supportedFeatures.push('weather');
      }

      return nodeDetails
        .concat(
          `\nCreative Id: ${creativeNode.creativeId ?? 'not specified'}${
            supportedFeatures.length ? `\nShows ${supportedFeatures.join(' and ')}` : ''
          }`,
        )
        .concat(
          `${
            creativeNode.isFallbackCreative
              ? 'This is a fallback creative, it should NEVER be connected to any other card'
              : ''
          }`,
        );
    }

    case NodeType.CONTENT_FEED: {
      const contentFeedNode = node as ContentFeedNode;
      const configuration = contentFeedNode.configuration;

      if (contentFeedNode.strategy === ContentSelectionStrategyValue.Matching && configuration) {
        nodeDetails.concat(
          `Purpose: Select content item according to the following conditions:`,
          configuration.conditions
            .map(
              (condition) =>
                `${condition.feedColumnSignalKey} -> ${getMatchingEnumValue(
                  ProductMatchingOperator,
                  ProductMatchingOperatorDescription,
                  condition.operator,
                )} -> ${condition.signalKey}`,
            )
            .join('\n'),
        );
      } else if (
        contentFeedNode.strategy === ContentSelectionStrategyValue.Specific &&
        contentFeedNode.contentId
      ) {
        nodeDetails.concat(`Purpose: Select content item ${contentFeedNode.contentId}`);
      }

      return nodeDetails.concat(
        contentFeedNode.outputs
          .map(
            (output) =>
              `Output ${_getOutputIdentifier(output)} ${getOutputTypePhrase(output)} ${getDestinationCardPhrase(
                output.nodeId,
              )}`,
          )
          .join('\n'),
      );
    }

    case NodeType.STORE: {
      const storeNode = node as DTStoreNode;

      if (loadedEnumsSubject.data[CLEnumType.CampaignPage]?.LocationTargetingMethod) {
        nodeDetails.concat(
          `Purpose: Select store by ${
            loadedEnumsSubject.data[CLEnumType.CampaignPage]?.LocationTargetingMethod.byValue[
              String(storeNode.locationTargetingMethod)
            ].name
          }`,
        );
      }

      return nodeDetails.concat(
        storeNode.outputs
          .map(
            (output) =>
              `Output ${_getOutputIdentifier(output)} ${getOutputTypePhrase(output)} ${getDestinationCardPhrase(
                output.nodeId,
              )}`,
          )
          .join('\n'),
      );
    }

    case NodeType.SCHEDULE: {
      const scheduleNode = node as DTScheduleNode;
      const scheduleDetails = nodeDetails.concat(`
Purpose: Select output by ${getMatchingEnumValue(
        ScheduleType,
        ScheduleTypeDescription,
        scheduleNode.scheduleType,
      )}`);

      return scheduleDetails.concat(
        scheduleNode.outputs
          .map((output: ScheduleNodeOutputData) => {
            const outputIdentifier = _getOutputIdentifier(output);

            if (output.dateRange) {
              return `Output ${outputIdentifier} ${getDestinationCardPhrase(
                output.nodeId,
              )} on ${output.dateRange.start.toISO()} to ${output.dateRange.end.toISO()}`;
            } else if (output.daysOfWeek) {
              return `Output ${outputIdentifier} ${getDestinationCardPhrase(
                output.nodeId,
              )} on ${output.daysOfWeek
                .map((day) => day.days.join(', '))
                .join(', ')} ${output.daysOfWeek
                .map((day) => day.start_minute)
                .join(', ')} to ${output.daysOfWeek.map((day) => day.end_minute).join(', ')}`;
            }

            return `Output ${outputIdentifier} ${getDestinationCardPhrase(output.nodeId)}`;
          })
          .join('\n'),
      );
    }

    case NodeType.SEQUENCE: {
      const sequecneNode = node as DTSequenceNode;

      return nodeDetails.concat(
        '\n'.concat(
          sequecneNode.outputs
            .map(
              (output: SequenceNodeOutputData) =>
                `Output ${_getOutputIdentifier(output)} - Repetitions: ${output.maxRepeats}`,
            )
            .join('\n'),
        ),
      );
    }

    default:
      return `Card Id: ${node.id} Name: ${node.name || `${cardTypeToName[node.type]} card`} Type: ${
        cardTypeToName[node.type]
      }`;
  }
}

function _getOutputIdentifier(output: {name?: string | number | null; id: number}): string {
  const namePart = output.name ? `'${output.name}' ` : '';

  return `${output.id} ${namePart}`;
}
